import type { AgentIteration, NodeExecution } from '@/types/monitoring';

/**
 * For chatMessage workflows (OMT), take the *latest* iteration's action
 * statuses and replace matching base action executions on the canvas so
 * badges reflect the current turn's progress. Non-action nodes
 * (triggers, orchestrator) and actions that haven't executed in the
 * latest iteration are preserved.
 *
 * Returns the base list unchanged when there are no iteration actions.
 */
export function applyChatMessageIterationActions(
  base: NodeExecution[],
  chatMessageIterationActions: Map<number, NodeExecution[]>
): NodeExecution[] {
  if (chatMessageIterationActions.size === 0) return base;

  const latestIterationIndex = Math.max(...Array.from(chatMessageIterationActions.keys()));
  const latestIterationActions = chatMessageIterationActions.get(latestIterationIndex) ?? [];

  const chatMessageExecutions: NodeExecution[] = latestIterationActions.map((exec) => ({
    ...exec,
    nodeId: `action-${exec.nodeName}`,
  }));
  const iterationActionNames = new Set(chatMessageExecutions.map((e) => e.nodeName));

  const filteredBase = base.filter((e) => {
    const isActionNode = e.nodeId.startsWith('action-');
    if (!isActionNode) return true;
    return !iterationActionNames.has(e.nodeName);
  });

  return [...filteredBase, ...chatMessageExecutions];
}

/**
 * Enrich existing agent nodes with the iteration data we've fetched
 * (attaches `iterationCount` and `iterations` fields).
 */
export function enrichAgentIterationCounts(
  base: NodeExecution[],
  agentIterationsData: Map<string, AgentIteration[]>
): NodeExecution[] {
  return base.map((exec) => {
    const iterations = agentIterationsData.get(exec.nodeName);
    if (iterations && iterations.length > 0) {
      return { ...exec, iterationCount: iterations.length, iterations };
    }
    return exec;
  });
}

const pickAgentStatus = (
  lastIteration: AgentIteration | undefined
): NodeExecution['status'] => {
  if (lastIteration?.status === 'succeeded') return 'succeeded';
  if (lastIteration?.status === 'failed') return 'failed';
  if (lastIteration?.status === 'running') return 'running';
  return 'running';
};

/**
 * For `kind: Agent` workflows, agent action nodes are stripped from the
 * run API response. Synthesize NodeExecutions for those agents from the
 * iteration timeline so they still appear on the canvas.
 *
 * Only synthesizes agents that aren't already represented in the base
 * result and that actually have iterations.
 */
export function synthesizeAgentNodeExecutions(
  base: NodeExecution[],
  agentIterationsData: Map<string, AgentIteration[]>
): NodeExecution[] {
  const existingNodeNames = new Set(base.map((e) => e.nodeName));
  const synthesized: NodeExecution[] = [];

  for (const [agentName, iterations] of agentIterationsData.entries()) {
    if (existingNodeNames.has(agentName) || iterations.length === 0) continue;
    const first = iterations[0];
    const last = iterations[iterations.length - 1];
    synthesized.push({
      nodeId: `action-${agentName}`,
      nodeName: agentName,
      nodeType: 'Agent',
      status: pickAgentStatus(last),
      startTime: first?.startTime,
      endTime: last?.endTime,
      iterationCount: iterations.length,
      iterations,
    });
  }

  return [...base, ...synthesized];
}

/**
 * For each scope where the user has picked a specific iteration on the
 * canvas, overlay that iteration's child action status/timings onto the
 * base canvas node. Only overrides values that are *defined* on the
 * iteration record so a running iteration doesn't blank out aggregate
 * duration/end.
 *
 * The lookup index is keyed by bare action name so canvas nodes that
 * carry scoped ids (e.g. `IsApproved/Compose_1`) still find their
 * override via the executionLookup fallback.
 */
export function applyScopeIterationOverrides(
  base: NodeExecution[],
  scopeActions: Map<string, NodeExecution[]>,
  selectedScopeRepetitions: Record<string, string>
): NodeExecution[] {
  if (!selectedScopeRepetitions || Object.keys(selectedScopeRepetitions).length === 0) {
    return base;
  }
  if (scopeActions.size === 0) return base;

  const iterationOverrides = new Map<string, NodeExecution>();
  for (const [scopeName, repetitionName] of Object.entries(selectedScopeRepetitions)) {
    const actionKey = `${scopeName}-${repetitionName}`;
    const iterationActions = scopeActions.get(actionKey);
    if (!iterationActions) continue;
    for (const childExec of iterationActions) {
      iterationOverrides.set(childExec.nodeName, childExec);
    }
  }
  if (iterationOverrides.size === 0) return base;

  return base.map((exec) => {
    const override = iterationOverrides.get(exec.nodeName);
    if (!override) return exec;
    const merged: NodeExecution = { ...exec, status: override.status };
    if (override.duration !== undefined) merged.duration = override.duration;
    if (override.startTime !== undefined) merged.startTime = override.startTime;
    if (override.endTime !== undefined) merged.endTime = override.endTime;
    return merged;
  });
}

export interface ComputeNodeExecutionsArgs {
  baseNodeExecutions: NodeExecution[];
  isChatMessageWorkflow: boolean;
  chatMessageIterationActions: Map<number, NodeExecution[]>;
  agentIterationsData: Map<string, AgentIteration[]> | null | undefined;
  scopeActions: Map<string, NodeExecution[]>;
  selectedScopeRepetitions: Record<string, string>;
}

/**
 * Compose the canvas-facing node execution list from the raw base data
 * plus the various enrichment sources (chat-message iterations, agent
 * timeline, scope repetitions).
 *
 * Order matters: chat-message replacement runs first (so the canvas shows
 * the latest turn), then agent iteration counts get merged in for existing
 * agents, then missing agents are synthesized, and finally scope
 * repetition overlays are applied so the user's iteration selection wins.
 */
export function computeNodeExecutions(args: ComputeNodeExecutionsArgs): NodeExecution[] {
  const {
    baseNodeExecutions,
    isChatMessageWorkflow,
    chatMessageIterationActions,
    agentIterationsData,
    scopeActions,
    selectedScopeRepetitions,
  } = args;

  let result = baseNodeExecutions;

  if (isChatMessageWorkflow) {
    result = applyChatMessageIterationActions(result, chatMessageIterationActions);
  }

  if (agentIterationsData && agentIterationsData.size > 0) {
    result = enrichAgentIterationCounts(result, agentIterationsData);
    result = synthesizeAgentNodeExecutions(result, agentIterationsData);
  }

  result = applyScopeIterationOverrides(result, scopeActions, selectedScopeRepetitions);
  return result;
}
